import React, { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import type { CategorieMarqueur } from '../../types/map';
import { MIN_SEARCH_CHARS } from './searchTypes';
import { CategoriesPickerSheet } from './CategoriesPickerSheet';
import './SearchPanel.css';
import { resolveAssetUrl } from '../../config/api';

/** Fond de carte minimal nécessaire au panneau (campus). */
export interface SearchPanelBaseMap {
  id?: string;
  nom: string;
}

export interface SearchPanelProps {
  open: boolean;
  onClose: () => void;
  /** Fonds de carte (campus) — radios, un seul actif. */
  baseMaps: SearchPanelBaseMap[];
  activeBaseMapId: string | undefined;
  onBaseMapChange: (id: string | undefined) => void;
  /** Catégories (living lab) — checkboxes. */
  categories: CategorieMarqueur[];
  checkedCategorieIds: Set<number>;
  onToggleCategorie: (id: number) => void;
  /** Recherche textuelle (contrôlée). */
  keywords: string;
  onKeywordsChange: (kw: string) => void;
  onSubmitSearch: () => void;
  /** Mots-clés figés de la dernière recherche validée (pilote loupe/croix, §3.6). */
  submittedKeywords?: string;
  /** Effacer la recherche en cours (clic sur la croix, §3.6). */
  onClearSearch?: () => void;
  /** Illustration chargée en back-office (§3.3) ; fallback neutre si absente. */
  illustrationUrl?: string;
  /** Illustration dédiée à la version mobile (§3.3) ; fallback = illustrationUrl. */
  illustrationUrlMobile?: string;
}

/**
 * Panneau « Votre recherche » (SPEC §3) : campus (fond de carte), living lab
 * (catégories) et recherche textuelle. Composant CONTRÔLÉ — aucune logique de
 * filtrage ni d'appel API (gérés en amont, W0.2/W0.3).
 */
export const SearchPanel: React.FC<SearchPanelProps> = ({
  open,
  onClose,
  baseMaps,
  activeBaseMapId,
  onBaseMapChange,
  categories,
  checkedCategorieIds,
  onToggleCategorie,
  keywords,
  onKeywordsChange,
  onSubmitSearch,
  submittedKeywords = '',
  onClearSearch = () => {},
  illustrationUrl,
  illustrationUrlMobile,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  // Mobile uniquement (§6.1) — tiroir plein écran pour la sélection des
  // catégories, afin d'éviter l'ascenseur imbriqué en cas d'iframe bas
  // (ex. 550px de haut) et de rester scalable au-delà de 7 catégories.
  const [categoriesSheetOpen, setCategoriesSheetOpen] = useState(false);

  // Clignotement rouge du champ de recherche quand on valide une recherche
  // vide (§3.6). Réinitialisé via requestAnimationFrame pour pouvoir se
  // redéclencher même si un clignotement précédent est encore en cours.
  const [inputError, setInputError] = useState(false);
  const triggerShake = useCallback(() => {
    setInputError(false);
    requestAnimationFrame(() => setInputError(true));
  }, []);

  // Croix (effacer) tant que la recherche validée correspond au texte affiché ;
  // dès que l'utilisateur modifie le champ, on repasse en loupe (§3.6).
  const isSearchActive = submittedKeywords.trim() !== '' && keywords === submittedKeywords;

  // À l'ouverture : focus sur le premier contrôle (SPEC §6.3).
  useEffect(() => {
    if (!open) return;
    const first = bodyRef.current?.querySelector<HTMLElement>('input, button, [tabindex]');
    first?.focus();
  }, [open]);

  // W3.3 — Mémoïser les callbacks pour éviter les re-renders des enfants (checkboxes/radios)
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    }
  }, [onClose]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (keywords.trim() === '') {
      triggerShake();
      return;
    }
    onSubmitSearch();
  }, [keywords, onSubmitSearch, triggerShake]);

  const handleClearSearch = useCallback(() => {
    onClearSearch();
  }, [onClearSearch]);

  // W3.3 — Mémoïser le groupe de radios pour éviter re-renders au changement d'autres props
  const radioGroup = useMemo(
    () => (
      <div role="radiogroup" aria-label="Choisissez votre campus">
        {baseMaps.map((bm, i) => {
          const id = bm.id ?? String(i);
          return (
            <label key={id} className="search-panel__option">
              <input
                type="radio"
                name="campus"
                value={id}
                checked={activeBaseMapId === bm.id}
                onChange={() => onBaseMapChange(bm.id)}
              />
              <span>{bm.nom}</span>
            </label>
          );
        })}
      </div>
    ),
    [baseMaps, activeBaseMapId, onBaseMapChange]
  );

  // Mobile (§6.1) — sélecteur <select> équivalent au groupe de radios ci-dessus.
  const campusSelectValue = useMemo(() => {
    const idx = baseMaps.findIndex((bm) => bm.id === activeBaseMapId);
    return idx >= 0 ? baseMaps[idx].id ?? String(idx) : '';
  }, [baseMaps, activeBaseMapId]);

  const handleCampusSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const val = e.target.value;
      const idx = baseMaps.findIndex((bm, i) => (bm.id ?? String(i)) === val);
      if (idx >= 0) onBaseMapChange(baseMaps[idx].id);
    },
    [baseMaps, onBaseMapChange]
  );

  const campusSelect = useMemo(
    () => (
      <select
        className="search-panel__select"
        aria-label="Choisissez votre campus"
        value={campusSelectValue}
        onChange={handleCampusSelectChange}
      >
        {baseMaps.map((bm, i) => {
          const id = bm.id ?? String(i);
          return (
            <option key={id} value={id}>
              {bm.nom}
            </option>
          );
        })}
      </select>
    ),
    [baseMaps, campusSelectValue, handleCampusSelectChange]
  );

  // W3.3 — Mémoïser le groupe de checkboxes pour éviter re-renders
  const checkboxGroup = useMemo(
    () =>
      categories.map((cat) => (
        <label key={cat.id} className="search-panel__option">
          <input
            type="checkbox"
            checked={cat.id != null && checkedCategorieIds.has(cat.id)}
            onChange={() => cat.id != null && onToggleCategorie(cat.id)}
          />
          <span className="search-panel__option-icon" aria-hidden="true">
            {cat.typeIcone === 'image' && cat.imageMarqueur ? (
              <img
                src={resolveAssetUrl(cat.imageMarqueur)}
                alt=""
                className="search-panel__option-icon--img"
              />
            ) : (
              <span
                className="search-panel__option-icon--dot"
                style={{ background: cat.couleur || '#FF5733' }}
              />
            )}
          </span>
          <span>{cat.nom}</span>
        </label>
      )),
    [categories, checkedCategorieIds, onToggleCategorie]
  );

  return (
    <section
      id="search-panel"
      className={`search-panel${open ? ' is-open' : ''}`}
      aria-label="Votre recherche"
      aria-hidden={!open}
      onKeyDown={handleKeyDown}
    >
      <div className="search-panel__header">
        <button
          type="button"
          className="search-panel__collapse"
          aria-label="Replier le panneau de recherche"
          onClick={onClose}
        >
          <img src="/arrow-right.svg" alt="" className="search-panel__collapse-icon" aria-hidden="true" />
        </button>
      </div>

      <div className="search-panel__body" ref={bodyRef}>
        {/* Illustration (décorative) — variante mobile dédiée si fournie (§3.3) */}
        {illustrationUrl || illustrationUrlMobile ? (
          <picture>
            {illustrationUrlMobile && (
              <source media="(max-width: 640px)" srcSet={illustrationUrlMobile} />
            )}
            <img
              className="search-panel__illustration"
              src={illustrationUrl || illustrationUrlMobile}
              alt=""
              aria-hidden="true"
            />
          </picture>
        ) : (
          <div className="search-panel__illustration search-panel__illustration--fallback" aria-hidden="true" />
        )}

        {/* Campus = fond de carte (radios desktop / select mobile, §6.1) */}
        <fieldset className="search-panel__zone search-panel__zone--scroll">
          <legend className="search-panel__title">Choisissez votre campus</legend>
          <div className="search-panel__options-desktop">{radioGroup}</div>
          <div className="search-panel__options-mobile">{campusSelect}</div>
        </fieldset>

        {/* Living lab = catégories (checkboxes desktop / tiroir dédié mobile, §6.1) */}
        <fieldset className="search-panel__zone search-panel__zone--scroll">
          <legend className="search-panel__title">Choisissez votre living lab</legend>
          <div className="search-panel__options-desktop">
            <div className="search-panel__options">{checkboxGroup}</div>
          </div>
          <div className="search-panel__options-mobile">
            <button
              type="button"
              className="search-panel__categories-trigger"
              onClick={() => setCategoriesSheetOpen(true)}
              aria-haspopup="dialog"
            >
              <span>Living lab</span>
              <span className="search-panel__categories-trigger-count" aria-hidden="true">
                {checkedCategorieIds.size}/{categories.length}
              </span>
            </button>
          </div>
        </fieldset>

        {/* Recherche textuelle */}
        <form className="search-panel__search" role="search" onSubmit={handleSubmit}>
          <input
            type="text"
            className={`search-panel__search-input${inputError ? ' search-panel__search-input--error' : ''}`}
            placeholder="Rechercher…"
            aria-label="Rechercher"
            aria-describedby="search-panel-hint"
            value={keywords}
            onChange={(e) => onKeywordsChange(e.target.value)}
            onAnimationEnd={() => setInputError(false)}
          />
          <button
            type={isSearchActive ? 'button' : 'submit'}
            className="search-panel__search-btn"
            aria-label={isSearchActive ? 'Effacer la recherche' : 'Lancer la recherche'}
            onClick={isSearchActive ? handleClearSearch : undefined}
          >
            <img
              src={isSearchActive ? '/close.svg' : '/magnifier.svg'}
              alt=""
              className="search-panel__search-btn-icon"
              aria-hidden="true"
            />
          </button>
          <p id="search-panel-hint" className="search-panel__hint">
           {MIN_SEARCH_CHARS} caractères minimum pour lancer la recherche.
          </p>
        </form>
      </div>

      {categoriesSheetOpen && (
        <CategoriesPickerSheet
          categories={categories}
          checkedCategorieIds={checkedCategorieIds}
          onToggleCategorie={onToggleCategorie}
          onClose={() => setCategoriesSheetOpen(false)}
        />
      )}
    </section>
  );
};

export default SearchPanel;
